import { gql } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import {
  OrgConfigQuery,
  OrganizationalUnitsQuery,
  useCreateShiftCenterMutation,
  useUpdateShiftCenterMutation,
} from "~/graphql/__generated__/graphql";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import { pick } from "lodash";

import AddressForm from "~/components/common/forms/AddressForm";
import { AddressData, addressKeys } from "@caredna-marlin/utils/client/geocoding";

type ShiftCenterConfigFormDialogProps = {
  onClose: () => void;
  currentShiftCenter: OrgConfigQuery["shiftCenter"][number] | null;
  organizationalUnits: OrganizationalUnitsQuery["organizationalUnit"];
};

type ShiftCenterConfig = {
  id?: number;
  name?: string;
  organizationalUnitId?: number | null;
} & AddressData;

const ShiftCenterConfigFormDialog = ({
  onClose,
  currentShiftCenter,
  organizationalUnits,
}: ShiftCenterConfigFormDialogProps) => {
  const [createMutation, { loading: createLoading }] = useCreateShiftCenterMutation();
  const [updateMutation, { loading: updateLoading }] = useUpdateShiftCenterMutation();

  const [error, setError] = useState<string | null>(null);

  const loading = createLoading || updateLoading;

  const nameFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimeout(() => nameFieldRef.current?.focus());
  }, []);

  const [local, setLocal] = useState<ShiftCenterConfig>(currentShiftCenter || {});
  const activeOrganizationalUnits = organizationalUnits.filter(unit => unit.active);
  const organizationalUnitId = local.id
    ? local.organizationalUnitId ?? null
    : local.organizationalUnitId ?? (activeOrganizationalUnits.length === 1 ? activeOrganizationalUnits[0].id : null);
  const organizationalUnitLabel = organizationalUnits.length ? "Organizational Unit" : "Organizational Unit (not configured)";

  const onSubmit = async () => {
    if (loading) { return; }

    const variables = {
      name: local.name!,
      organizationalUnitId,
      addressStreet: local.addressStreet!,
      addressCity: local.addressCity ?? null,
      addressState: local.addressState ?? null,
      addressZip: local.addressZip ?? null,
    };

    try {
      if (local.id) {
        await updateMutation({
          variables: { ...variables, id: local.id },
          refetchQueries: ["OrgConfig"],
          awaitRefetchQueries: true,
        });
      } else {
        await createMutation({
          variables,
          refetchQueries: ["OrgConfig"],
          awaitRefetchQueries: true,
        });
      }
    } catch (error) {
      // @ts-ignore
      const message = error?.networkError?.result?.errors?.[0]?.message;
      if (message) {
        setError(message);
      }
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="xs"
      onClose={onClose}
    >
      <DialogTitle>
        {local.id ? "Edit Shift Center" : "New Shift Center"}
      </DialogTitle>
      <DialogContent>
        <TextField
          required
          margin="normal"
          label="Name/Label"
          fullWidth
          variant="standard"
          autoCorrect="off"
          inputRef={nameFieldRef}
          disabled={loading}
          value={local?.name || ""}
          onChange={(e) => setLocal({...local, name: e.target.value})}
        />
        <AddressForm
          address={pick(local, addressKeys)}
          setAddress={(address) => setLocal({...local, ...address })}
          disabled={loading}
          skipLineTwo
        />
        <FormControl
          margin="normal"
          fullWidth
          variant="standard"
        >
          <InputLabel id="shift-center-organizational-unit-label">{organizationalUnitLabel}</InputLabel>
          <Select<number | "">
            labelId="shift-center-organizational-unit-label"
            name="organizationalUnitId"
            label={organizationalUnitLabel}
            value={organizationalUnitId ?? ""}
            disabled={loading || organizationalUnits.length === 0}
            onChange={(e) => setLocal({ ...local, organizationalUnitId: Number(e.target.value) })}
          >
            {organizationalUnits.map(unit => (
              <MenuItem
                key={unit.id}
                value={unit.id}
                disabled={!unit.active && unit.id !== organizationalUnitId}
              >
                {unit.name}{unit.active ? "" : " (retired)"}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button disabled={loading || (!local.id && activeOrganizationalUnits.length > 0 && organizationalUnitId == null)} onClick={onSubmit}>
          {local.id ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

gql(`
  mutation CreateShiftCenter($name: String!, $organizationalUnitId: Int, $addressStreet: String!, $addressCity: String, $addressState: String, $addressZip: String) @permission(requires: manageOrgConfig) {
    createShiftCenter(name: $name, organizationalUnitId: $organizationalUnitId, addressStreet: $addressStreet, addressCity: $addressCity, addressState: $addressState, addressZip: $addressZip) {
      id
      name
      organizationalUnitId
      addressStreet
      addressCity
      addressState
      addressZip
      addressCoordinates
    }
  }
`);

gql(`
  mutation UpdateShiftCenter($id: Int!, $name: String, $organizationalUnitId: Int, $addressStreet: String, $addressCity: String, $addressState: String, $addressZip: String) @permission(requires: manageOrgConfig) {
    updateShiftCenter(id: $id, name: $name, organizationalUnitId: $organizationalUnitId, addressStreet: $addressStreet, addressCity: $addressCity, addressState: $addressState, addressZip: $addressZip) {
      id
      name
      organizationalUnitId
      addressStreet
      addressCity
      addressState
      addressZip
      addressCoordinates
    }
  }
`);

export default ShiftCenterConfigFormDialog;
